/** @jsxImportSource @gongxigames/framework/core/fragments/BuilderY */
/** @jsxRuntime automatic */

import {
    FC,
    $Sprite,
    CanBeSignals,
    SpriteProps,
    createPropsGetter,
    getProp
} from '@gongxigames/framework/core/fragments/BuilderY';
import {Store} from '@gongxigames/framework/core/fragments/store/Store';
import {slotStore} from '@gongxigames/framework/slotV2/globals';
import {Easing} from '@gongxigames/framework/core';
import {Sprite as PixiSprite, utils} from 'pixi.js';
import {DISABLED_ALPHA, TOGGLE_COLOR_FALSE, TOGGLE_COLOR_TRUE} from '@gongxigames/framework/slotV2/ui/ui-ruby/CONSTANTS';

export type SwitchProps = SpriteProps & CanBeSignals<{
    name: string;
    valueAutoUpdate?: boolean;
    value?: boolean;
    disabled?: boolean;
    action?: (val: boolean) => void;
}>;

const ANIMATION_DURATION = 16*16;
const OFFSET_X = 21;
const OFFSET_Y = 1;

const TOGGLE_COLOR_FALSE_RGB = utils.hex2rgb(TOGGLE_COLOR_FALSE, [0,0,0]);
const TOGGLE_COLOR_TRUE_RGB = utils.hex2rgb(TOGGLE_COLOR_TRUE, [0,0,0]);

export const $Switch: FC<SwitchProps> = (props) => {
    const hover = Store.signal(false);
    const enabled = Store.computed(() => !getProp(props.disabled));
    const spriteBG = new PixiSprite();
    const spriteSwitch = new PixiSprite();
    const currentColor = [0,0,0];

    props.value ??= Store.signal(false);

    const offAlpha = Store.effect(() => {
        const alpha = getProp(props.disabled) ? DISABLED_ALPHA : 1;

        slotStore.updateManager.value!
            .addTween(spriteBG)
            .to({alpha}, 50)
            .start();
    });

    const setHoverTrue = () => hover.value = true;
    const setHoverFalse = () => hover.value = false;

    const toggle = () => {
        const {action, valueAutoUpdate} = createPropsGetter(props);
        const newVal = !getProp(props.value);

        if (valueAutoUpdate) {
            if (Store.isNotReadonlySignal(props.value)) {
                (props.value as any).value = newVal;
            }
        }

        action?.(newVal);
    };

    const reset = () => {
        const {value} = createPropsGetter(props);

        spriteBG.tint = value ? TOGGLE_COLOR_TRUE : TOGGLE_COLOR_FALSE;
        spriteSwitch.x = value ? OFFSET_X : -OFFSET_X;

        return true;
    };

    const offAnimation = Store.effect(() => {
        const {value} = createPropsGetter(props);
        const color = value ? TOGGLE_COLOR_TRUE_RGB : TOGGLE_COLOR_FALSE_RGB;
        const offset = value ? OFFSET_X : -OFFSET_X;

        utils.hex2rgb(spriteBG.tint, currentColor);

        slotStore.updateManager.value!
            .addTween(currentColor)
            .to(color, ANIMATION_DURATION)
            .onUpdate(() => {
                spriteBG.tint = utils.rgb2hex(currentColor);
            })
            .start()
            .onComplete(reset);

        slotStore.updateManager.value!
            .addTween(spriteSwitch)
            .to({x: offset}, ANIMATION_DURATION)
            .onUpdate(() => {
                spriteBG.tint = utils.rgb2hex(currentColor);
            })
            .start()
            .easing(Easing.Elastic.Out)
            .onComplete(reset);
    });

    return <$Sprite
        instance={spriteBG}
        anchor={0.5}
        interactive={enabled}
        buttonMode={enabled}
        {...props}
        texture='switch_bg_white_sys_ui'
        events={{
            pointerover: setHoverTrue,
            pointerout: setHoverFalse,
            pointertap: toggle,
            destroyed: [offAlpha, offAnimation],
        }}
    >
        <$Sprite
            y={OFFSET_Y}
            instance={spriteSwitch}
            anchor={0.5}
            texture='switch_knob_sys_ui'
        />
    </$Sprite>;
};